import { t } from '../i18n';

const normalizeUtcDateString = (dateString) => {
  if (!dateString) return dateString;
  if (dateString instanceof Date) {
    return dateString.toISOString();
  }
  if (typeof dateString === 'number') {
    return new Date(dateString).toISOString();
  }
  const value = String(dateString);
  if (value.endsWith('Z') || value.includes('+') || value.match(/-\d{2}:\d{2}$/)) {
    return value;
  }
  return `${value}Z`;
};

export const formatCallDuration = (seconds) => {
  if (!seconds || seconds <= 0) return null;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const getCallTimelineLabel = (item) => {
  if (item.kind === 'group') {
    const duration = formatCallDuration(item.duration_seconds);
    return duration ? `${t('call.groupCall')} · ${duration}` : t('call.groupCall');
  }
  if (item.state === 'missed') {
    return item.direction === 'incoming' ? t('call.missed') : t('call.noAnswer');
  }
  if (item.state === 'rejected') {
    return t('call.declined');
  }
  if (item.end_reason === 'hangup' || item.state === 'ended') {
    const duration = formatCallDuration(item.duration_seconds);
    const direction = item.direction === 'outgoing' ? t('call.outgoingCall') : t('call.incomingCall');
    return duration ? `${direction} · ${duration}` : direction;
  }
  if (item.state === 'ringing' || item.state === 'connecting' || item.state === 'active') {
    return item.direction === 'outgoing' ? t('call.outgoingCall') : t('call.incomingCall');
  }
  return item.direction === 'outgoing' ? t('call.outgoingCall') : t('call.incomingCall');
};

export const isMissedCall = (item) => item.state === 'missed'
  || (item.state === 'rejected' && item.direction === 'incoming');

const toTimestamp = (dateString) => {
  const normalized = normalizeUtcDateString(dateString);
  const time = normalized ? new Date(normalized).getTime() : 0;
  return Number.isNaN(time) ? 0 : time;
};

const compareTimelineItems = (a, b) => {
  const ta = toTimestamp(a.sortAt);
  const tb = toTimestamp(b.sortAt);
  if (ta !== tb) return ta - tb;
  const sa = Number(a.client_seq) || 0;
  const sb = Number(b.client_seq) || 0;
  if (sa !== sb) return sa - sb;
  // Messages before calls when tied; stable id fallback.
  if (a.type !== b.type) {
    return a.type === 'message' ? -1 : 1;
  }
  return String(a.id || '').localeCompare(String(b.id || ''));
};

export const buildChatTimeline = (messages, callHistory = []) => {
  const timeline = [
    ...messages.map((msg) => ({
      type: 'message',
      sortAt: msg.created_at || msg.timestamp,
      id: msg.id,
      ...msg,
    })),
    ...callHistory.map((call) => ({
      type: 'call',
      sortAt: call.started_at,
      id: `call-${call.call_id}`,
      created_at: call.started_at,
      call,
    })),
  ];

  timeline.sort(compareTimelineItems);

  const grouped = [];
  let currentDate = null;
  let dateDividerIndex = 0;

  timeline.forEach((item) => {
    const normalizedDate = normalizeUtcDateString(item.sortAt || item.created_at);
    const itemDate = normalizedDate
      ? new Date(normalizedDate).toDateString()
      : new Date().toDateString();

    if (itemDate !== currentDate) {
      currentDate = itemDate;
      grouped.push({
        type: 'date',
        date: item.sortAt || item.created_at || new Date().toISOString(),
        id: `date-${currentDate}-${dateDividerIndex++}`,
      });
    }

    grouped.push(item);
  });

  return grouped;
};
